// src/components/map/layers/TournamentLayer.ts
// Big Blue tournament marker controller.
// Owns tournament-specific Leaflet markers, marker cache, popup DOM, and the
// display-only worldwide marker while MapView keeps the single map instance,
// discovery/filter state, preview React UI, and shared map coordination.

import L from 'leaflet';
import type { Tournament } from '../../../types';

type LatLng = { lat: number; lng: number };
type MapPoint = { x: number; y: number };

export type TournamentMapPresentation = {
  badge?: string;
  timer?: string;
  tone?: string;
  priority?: number;
  tooltip?: string;
};

export type TournamentMapRow = {
  tournament: Tournament | any;
  presentation: TournamentMapPresentation;
  worldwide?: boolean;
  distance?: number;
};

export type TournamentScopeBadge = {
  emoji: string;
  label: string;
};

type MarkerCacheEntry = {
  marker: any;
  signature: string;
};

export type TournamentLayerSyncOptions = {
  map: any;
  layer: any;
  localRows: TournamentMapRow[];
  worldRows: TournamentMapRow[];
  hideAll: boolean;
  isDarkMode: boolean;
  nowMs: number;
  userLatLng?: LatLng | null;
  participation?: Record<string, any>;
  resultsSeenRevision?: number;
  getTournamentLatLng: (tournament: Tournament | any) => LatLng | null;
  getPresentation: (tournament: Tournament | any) => TournamentMapPresentation;
  getScopeBadge: (tournament: Tournament | any) => TournamentScopeBadge;
  getOccupiedMapPoints: (map: any) => MapPoint[];
  clampPointToSafeBox: (point: MapPoint, size: any) => MapPoint;
  resolveMapMarkerCollision: (
    desired: MapPoint,
    occupied: MapPoint[],
    size: any,
    opts?: { minDistance?: number; seed?: number; maxRadius?: number },
  ) => MapPoint;
  getSafeInsetsPx: () => { top: number; right: number; bottom: number; left: number };
  focusMapCardNearMarker: (
    map: any,
    marker: any,
    node: HTMLElement,
    options?: { preferredGap?: number; focusBias?: number },
  ) => void;
  onBeforeLocalMarkerOpen: () => void;
  onBeforeWorldMarkerOpen: () => void;
  onOpenTournament: (tournamentId: string) => void;
  onOpenTournamentDock: () => void;
  onMarkResultsSeen: (tournamentId: string) => void;
};

export type TournamentLayerController = {
  sync: (options: TournamentLayerSyncOptions) => void;
  clear: (layer?: any | null) => void;
  closePopups: () => void;
  forEachMarker: (callback: (marker: any, key: string) => void) => void;
  getMarkers: () => any[];
  resetForLayerRebuild: () => void;
  dispose: (layer?: any | null) => void;
};

const WORLD_CACHE_KEY = '__world_tournaments_near_user__';

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const setMarkerBadgeVisible = (marker: any, visible: boolean) => {
  try {
    const markerNode = marker?.getElement?.() as HTMLElement | null;
    const badge = markerNode?.querySelector?.('.roddo-tournament-map-status-badge') as HTMLElement | null;
    if (!badge) return;
    badge.style.opacity = visible ? '1' : '0';
    badge.style.transform = visible
      ? 'translateX(-50%)'
      : 'translateX(-50%) translateY(4px)';
  } catch {}
};

const createTournamentMarkerIcon = (
  tournament: Tournament | any,
  presentation: TournamentMapPresentation,
  scopeBadge: TournamentScopeBadge,
  isDarkMode: boolean,
  nowMs: number,
) => {
  const tone = String(presentation?.tone || 'quiet');
  const isLive = tone === 'live';
  const size = isLive ? 54 : 49;
  const badgeTitle = String(presentation?.badge || '').trim();
  const badgeTimer = String(presentation?.timer || '').trim();
  const startMs = Number(tournament?.startDate || 0);
  const startDeltaMs = startMs - nowMs;
  const showStartingSoon =
    !isLive && Number.isFinite(startDeltaMs) && startDeltaMs > 0 && startDeltaMs <= 6 * 60 * 60_000;
  const criticalStartingSoon = showStartingSoon && startDeltaMs <= 60 * 60_000;
  const showBadge = isLive || showStartingSoon;
  const urgencyBadgeTitle = isLive ? 'LIVE' : showStartingSoon ? 'Starts' : badgeTitle;
  const urgencyBadgeTimer = isLive
    ? badgeTimer.replace(/^Ends:\s*/i, 'Ends · ')
    : showStartingSoon
      ? compactCountdown(startMs, nowMs)
      : badgeTimer;
  const showRegisteredMiniBadge = tone === 'joined';

  const toneStyle = (() => {
    if (tone === 'live') {
      return { badgeBg: '#179B67', badgeText: '#FFFFFF', badgeBorder: '#DDF8EB', glow: 'rgba(23,155,103,.30)', pinTop: '#FFD05A', pinBottom: '#C88108', ring: 'rgba(255,215,106,.26)' };
    }
    if (tone === 'open') {
      return { badgeBg: '#F6C84A', badgeText: '#6E4700', badgeBorder: '#FFF5C7', glow: 'rgba(246,190,50,.30)', pinTop: '#FFD45F', pinBottom: '#C9830D', ring: 'rgba(246,190,50,.26)' };
    }
    if (tone === 'joined') {
      return { badgeBg: '#2F80ED', badgeText: '#FFFFFF', badgeBorder: '#DCEBFF', glow: 'rgba(47,128,237,.26)', pinTop: '#FFD05A', pinBottom: '#C88108', ring: 'rgba(47,128,237,.22)' };
    }
    if (tone === 'pending') {
      return { badgeBg: '#D49A25', badgeText: '#FFF9E8', badgeBorder: '#FFF0BD', glow: 'rgba(212,154,37,.26)', pinTop: '#F5C453', pinBottom: '#B8790D', ring: 'rgba(212,154,37,.22)' };
    }
    if (tone === 'finished') {
      return { badgeBg: '#D85A64', badgeText: '#FFFFFF', badgeBorder: '#FFE0E2', glow: 'rgba(216,90,100,.24)', pinTop: '#E7B34A', pinBottom: '#A96C0A', ring: 'rgba(216,90,100,.18)' };
    }
    return { badgeBg: '#5B6577', badgeText: '#FFFFFF', badgeBorder: '#EDF0F5', glow: 'rgba(239,184,58,.22)', pinTop: '#F5C453', pinBottom: '#BD7D12', ring: 'rgba(239,184,58,.20)' };
  })();

  const trophySvg = `
    <svg viewBox="0 0 24 24" aria-hidden="true" style="width:${isLive ? 24 : 22}px;height:${isLive ? 24 : 22}px;display:block;overflow:visible;">
      <path d="M8 4.75H16V8.5C16 10.709 14.209 12.5 12 12.5C9.791 12.5 8 10.709 8 8.5V4.75Z" fill="rgba(255,215,106,.18)" stroke="#FFD76A" stroke-width="1.8" stroke-linejoin="round"/>
      <path d="M8 6H5.75C5.336 6 5 6.336 5 6.75V7.5C5 9.433 6.567 11 8.5 11M16 6H18.25C18.664 6 19 6.336 19 6.75V7.5C19 9.433 17.433 11 15.5 11" fill="none" stroke="#FFD76A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12 12.5V16M9.5 16H14.5M9 19H15" fill="none" stroke="#FFD76A" stroke-width="1.8" stroke-linecap="round"/>
    </svg>`;

  return (L as any).divIcon({
    className: 'roddo-tournament-map-icon',
    iconSize: [size, size + 14],
    iconAnchor: [Math.round(size / 2), size + 10],
    html: `
      <div style="position:relative;width:${size}px;height:${size + 14}px;overflow:visible;filter:drop-shadow(0 4px 7px rgba(15,23,42,.16));">
        <div style="position:absolute;left:50%;top:${isLive ? '-3px' : '-1px'};width:${size + 18}px;height:${size + 18}px;transform:translateX(-50%);border-radius:999px;background:radial-gradient(circle,${toneStyle.glow} 0%,rgba(255,255,255,0) 68%);pointer-events:none;"></div>
        ${showBadge ? `<div class="roddo-tournament-map-status-badge" style="position:absolute;left:50%;top:-40px;transform:translateX(-50%);z-index:5;min-width:${criticalStartingSoon ? '110px' : '118px'};min-height:34px;border-radius:13px;padding:5px 13px 5px;white-space:nowrap;display:grid;place-items:center;background:${criticalStartingSoon ? '#E85B47' : toneStyle.badgeBg};color:${criticalStartingSoon ? '#FFFFFF' : toneStyle.badgeText};border:1.5px solid ${criticalStartingSoon ? '#FFE2DC' : toneStyle.badgeBorder};box-shadow:${criticalStartingSoon ? '0 7px 18px rgba(232,91,71,.34),0 0 0 1px rgba(255,255,255,.30) inset' : '0 6px 14px rgba(15,23,42,.22),0 0 0 1px rgba(255,255,255,.28) inset'};transition:opacity 120ms ease,transform 120ms ease;pointer-events:none;"><div style="font-size:9px;font-weight:950;letter-spacing:.035em;line-height:1.05;">${urgencyBadgeTitle}</div>${urgencyBadgeTimer ? `<div style="margin-top:3px;font-size:7.5px;font-weight:850;letter-spacing:.02em;line-height:1.05;opacity:.94;">${urgencyBadgeTimer}</div>` : ''}</div>` : ''}
        ${showRegisteredMiniBadge ? `<div aria-hidden="true" title="Registered" style="position:absolute;left:-7px;top:0;z-index:7;width:19px;height:19px;border-radius:999px;display:grid;place-items:center;background:#2F80ED;color:#FFFFFF;border:1.5px solid #DCEBFF;box-shadow:0 3px 8px rgba(47,128,237,.30);font-size:11px;font-weight:950;line-height:1;">✓</div>` : ''}
        <div aria-hidden="true" title="${escapeHtml(scopeBadge.label)}" style="position:absolute;right:-7px;top:0;z-index:7;width:19px;height:19px;border-radius:999px;display:grid;place-items:center;background:${isDarkMode ? '#111B2D' : '#FFFDF8'};border:1.5px solid ${isDarkMode ? 'rgba(255,224,160,.48)' : 'rgba(169,110,8,.26)'};box-shadow:0 3px 8px rgba(15,23,42,.22);font-size:10px;line-height:1;">${scopeBadge.emoji}</div>
        <div style="position:absolute;left:50%;top:2px;width:${size - 2}px;height:${size - 2}px;transform:translateX(-50%) rotate(-45deg);border-radius:50% 50% 50% 10%;background:linear-gradient(145deg,${toneStyle.pinTop} 0%,${toneStyle.pinBottom} 84%);border:2px solid rgba(255,255,255,.98);box-shadow:0 7px 16px rgba(95,59,8,.24),0 0 0 4px ${toneStyle.ring};">
          <div style="position:absolute;inset:5px;border-radius:999px;background:${isDarkMode ? 'linear-gradient(145deg,#1A2438 0%,#0F1728 100%)' : 'linear-gradient(145deg,#243552 0%,#142238 100%)'};display:grid;place-items:center;transform:rotate(45deg);overflow:hidden;box-shadow:inset 0 0 0 1px rgba(255,255,255,.10),0 2px 5px rgba(15,23,42,.20);">
            <div style="display:grid;place-items:center;transform:translateY(-.5px);">${trophySvg}</div>
          </div>
        </div>
      </div>`,
  });
};

const createWorldIcon = (count: number, isDarkMode: boolean) => {
  const size = 50;
  const safeCount = Math.max(1, Math.floor(Number(count || 1)));
  return (L as any).divIcon({
    className: 'roddo-tournament-map-icon roddo-world-near-user-icon',
    iconSize: [size, size + 14],
    iconAnchor: [Math.round(size / 2), size + 10],
    html: `
      <div style="position:relative;width:${size}px;height:${size + 14}px;overflow:visible;filter:drop-shadow(0 5px 9px rgba(15,23,42,.22));">
        <div style="position:absolute;left:50%;top:-1px;width:${size + 18}px;height:${size + 18}px;transform:translateX(-50%);border-radius:999px;background:radial-gradient(circle,rgba(246,190,50,.30) 0%,rgba(255,255,255,0) 68%);pointer-events:none;"></div>
        <div aria-hidden="true" title="Worldwide tournament" style="position:absolute;right:-7px;top:0;z-index:7;width:20px;height:20px;border-radius:999px;display:grid;place-items:center;background:${isDarkMode ? '#111B2D' : '#FFFDF8'};border:1.5px solid ${isDarkMode ? 'rgba(255,224,160,.48)' : 'rgba(169,110,8,.26)'};box-shadow:0 3px 8px rgba(15,23,42,.22);font-size:10px;line-height:1;">🌎</div>
        <div style="position:absolute;left:50%;top:2px;width:${size - 2}px;height:${size - 2}px;transform:translateX(-50%) rotate(-45deg);border-radius:50% 50% 50% 10%;background:linear-gradient(145deg,#FFD45F 0%,#C9830D 84%);border:2px solid rgba(255,255,255,.98);box-shadow:0 7px 16px rgba(95,59,8,.24),0 0 0 4px rgba(246,190,50,.25);">
          <div style="position:absolute;inset:5px;border-radius:999px;background:${isDarkMode ? 'linear-gradient(145deg,#1A2438 0%,#0F1728 100%)' : 'linear-gradient(145deg,#243552 0%,#142238 100%)'};display:grid;place-items:center;transform:rotate(45deg);overflow:hidden;box-shadow:inset 0 0 0 1px rgba(255,255,255,.10),0 2px 5px rgba(15,23,42,.20);">
            <svg viewBox="0 0 24 24" aria-hidden="true" style="width:22px;height:22px;display:block;overflow:visible;"><path d="M8 4.75H16V8.5C16 10.709 14.209 12.5 12 12.5C9.791 12.5 8 10.709 8 8.5V4.75Z" fill="rgba(255,215,106,.18)" stroke="#FFD76A" stroke-width="1.8" stroke-linejoin="round"/><path d="M8 6H5.75C5.336 6 5 6.336 5 6.75V7.5C5 9.433 6.567 11 8.5 11M16 6H18.25C18.664 6 19 6.336 19 6.75V7.5C19 9.433 17.433 11 15.5 11" fill="none" stroke="#FFD76A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 12.5V16M9.5 16H14.5M9 19H15" fill="none" stroke="#FFD76A" stroke-width="1.8" stroke-linecap="round"/></svg>
          </div>
        </div>
        <div style="position:absolute;left:-5px;bottom:0;z-index:8;min-width:20px;height:20px;padding:0 5px;border-radius:999px;display:grid;place-items:center;background:${isDarkMode ? '#111B2D' : '#FFFDF8'};border:1.5px solid ${isDarkMode ? 'rgba(255,224,160,.38)' : 'rgba(169,110,8,.22)'};box-shadow:0 3px 8px rgba(15,23,42,.18);color:${isDarkMode ? '#FFE0A0' : '#98630A'};font-size:8px;font-weight:950;line-height:1;">${safeCount > 99 ? '99+' : safeCount}</div>
      </div>`,
  });
};

const compactCountdown = (targetMs: number, nowMs: number): string => {
  if (!Number.isFinite(targetMs) || targetMs <= 0) return '';
  const delta = targetMs - nowMs;
  if (delta <= 0) return 'now';
  const minutes = Math.max(1, Math.floor(delta / 60_000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const buildTournamentPopupHtml = (
  tournament: Tournament | any,
  tournamentId: string,
  presentation: TournamentMapPresentation,
  participation: Record<string, any>,
  isWorldwide: boolean,
  isDarkMode: boolean,
  nowMs: number,
) => {
  const safeName = escapeHtml(tournament?.name || 'Roddo Tournament');
  const safeLocation = escapeHtml(
    isWorldwide ? 'Worldwide' : tournament?.locationLabel || tournament?.mapLocation?.label || 'Tournament area',
  );
  const status = String(tournament?.status || '').trim().toLowerCase();
  const participantStatus = String(participation[String(tournamentId || '')]?.status || '').trim().toLowerCase();
  const statusLabel = status === 'registration_open'
    ? 'Registration Open'
    : status === 'active' || status === 'live'
      ? 'Live Tournament'
      : participantStatus === 'pending'
        ? 'Registration Pending'
        : participantStatus === 'approved' && Number(tournament?.startDate || 0) > nowMs
          ? 'Registered'
          : presentation.badge === 'RESULTS'
            ? 'Results Ready'
            : presentation.badge === 'FINAL'
              ? 'Final Results'
              : presentation.badge || 'Tournament';
  const timerLabel = String(presentation.timer || '')
    .replace(/^Closes:\s*/i, 'Closes · ')
    .replace(/^Starts:\s*/i, 'Starts · ')
    .replace(/^Ends:\s*/i, 'Ends · ')
    .trim();
  const safeStatus = escapeHtml(statusLabel);
  const safeTimer = escapeHtml(timerLabel);
  const rawEntryFee = Number(tournament?.entryFeeShells ?? tournament?.entryFee ?? tournament?.shellEntryFee ?? tournament?.entryFeeAmount ?? 0);
  const entryLabel = Number.isFinite(rawEntryFee) && rawEntryFee > 0 ? `${Math.round(rawEntryFee)} shells` : 'Free entry';
  const safeEntry = escapeHtml(entryLabel);
  const projectedApprovedRaw = Number(tournament?.approvedParticipantCount);
  const hasProjectedApproved = Number.isFinite(projectedApprovedRaw) && projectedApprovedRaw >= 0;
  const projectedApproved = hasProjectedApproved ? Math.max(0, Math.round(projectedApprovedRaw)) : 0;
  const maxParticipantsRaw = Number(tournament?.maxParticipants);
  const hasMaxParticipants = Number.isFinite(maxParticipantsRaw) && maxParticipantsRaw > 0;
  const anglerLabel = hasProjectedApproved
    ? hasMaxParticipants
      ? `${projectedApproved}/${Math.round(maxParticipantsRaw)} anglers`
      : `${projectedApproved} angler${projectedApproved === 1 ? '' : 's'}`
    : '';
  const safeAnglers = escapeHtml(anglerLabel);
  const formatMiniDate = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) return '';
    try {
      return new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  };
  const startLabel = formatMiniDate(Number(tournament?.startDate || 0));
  const endLabel = formatMiniDate(Number(tournament?.endDate || 0));
  const scheduleLabel = [startLabel ? `Starts ${startLabel}` : '', endLabel ? `Ends ${endLabel}` : ''].filter(Boolean).join(' · ');
  const safeSchedule = escapeHtml(scheduleLabel);
  const nameColor = isDarkMode ? '#F4F7FB' : '#172033';
  const statusColor = isDarkMode ? '#78B8FF' : '#216FD1';
  const timerColor = isDarkMode ? '#F7D477' : '#9A6500';
  const mutedColor = isDarkMode ? '#AEBBD0' : '#667085';
  const chipBg = isDarkMode ? 'rgba(255,255,255,.085)' : '#F6F7F9';
  const chipText = isDarkMode ? '#E6ECF5' : '#475467';
  const trophyBg = isDarkMode ? 'rgba(246,202,104,.12)' : '#FFF8E5';
  const trophyBorder = isDarkMode ? 'rgba(246,202,104,.26)' : 'rgba(200,140,24,.18)';

  return `<div style="width:236px;padding:5px 4px 4px;font-family:inherit;"><div style="display:flex;align-items:flex-start;gap:10px;"><div style="width:40px;height:40px;flex:0 0 40px;border-radius:12px;display:grid;place-items:center;background:${trophyBg};border:1px solid ${trophyBorder};font-size:22px;">🏆</div><div style="min-width:0;flex:1;"><div style="font-size:13px;font-weight:800;line-height:1.2;color:${nameColor};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${safeName}</div><div style="margin-top:4px;font-size:10.5px;font-weight:800;line-height:1.2;color:${statusColor};">${safeStatus}</div>${safeTimer ? `<div style="margin-top:2px;font-size:10px;font-weight:800;line-height:1.2;color:${timerColor};">⏱ ${safeTimer}</div>` : ''}<div style="margin-top:4px;font-size:10px;font-weight:600;color:${mutedColor};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${safeLocation}</div></div></div><div style="margin-top:9px;display:flex;gap:6px;flex-wrap:wrap;"><span style="border-radius:999px;background:${chipBg};color:${chipText};padding:4px 7px;font-size:9px;font-weight:700;">${safeEntry}</span>${safeAnglers ? `<span style="border-radius:999px;background:${chipBg};color:${chipText};padding:4px 7px;font-size:9px;font-weight:700;">👥 ${safeAnglers}</span>` : ''}${safeSchedule ? `<span style="border-radius:999px;background:${chipBg};color:${chipText};padding:4px 7px;font-size:9px;font-weight:700;">${safeSchedule}</span>` : ''}</div><button type="button" data-roddo-open-tournament="${escapeHtml(tournamentId)}" style="margin-top:10px;width:100%;border:0;border-radius:10px;background:${isDarkMode ? '#F5D77C' : '#FFF1C8'};color:#694500;padding:9px 10px;font-size:10px;font-weight:800;cursor:pointer;">View tournament</button></div>`;
};

const createWorldPopupContent = (
  marker: any,
  rows: TournamentMapRow[],
  isDarkMode: boolean,
  onOpenTournament: (id: string) => void,
  onOpenTournamentDock: () => void,
) => {
  const root = document.createElement('div');
  root.setAttribute('data-roddo-world-tournament-popup', 'true');
  const text = isDarkMode ? '#F3F6FB' : '#2D3957';
  const muted = isDarkMode ? '#9CAAC0' : '#786D62';
  const border = isDarkMode ? 'rgba(142,156,211,.18)' : 'rgba(101,77,49,.12)';

  const rowHtml = rows.map(({ tournament, presentation }) => {
    const id = String(tournament?.id || '').trim();
    const name = escapeHtml(String(tournament?.name || 'Worldwide Tournament'));
    const rawBadge = String(presentation?.badge || presentation?.tooltip || 'Tournament').trim();
    const status = rawBadge === 'Join Now!' ? 'Registration Open' : rawBadge;
    const timer = String(presentation?.timer || '')
      .replace(/^Closes:\s*/i, 'Closes · ')
      .replace(/^Starts:\s*/i, 'Starts · ')
      .replace(/^Ends:\s*/i, 'Ends · ')
      .trim();
    const fee = Number(tournament?.entryFeeShells ?? tournament?.entryFee ?? tournament?.shellEntryFee ?? 0);
    const feeLabel = Number.isFinite(fee) && fee > 0 ? `${Math.round(fee)} shells` : 'Free entry';
    const approved = Number(tournament?.approvedParticipantCount);
    const max = Number(tournament?.maxParticipants);
    const anglers = Number.isFinite(approved) && approved >= 0
      ? Number.isFinite(max) && max > 0 ? `${Math.round(approved)}/${Math.round(max)} anglers` : `${Math.round(approved)} anglers`
      : '';
    return `<button type="button" data-roddo-open-world-tournament="${escapeHtml(id)}" style="width:100%;display:flex;align-items:center;gap:9px;border:1px solid ${border};border-radius:12px;background:${isDarkMode ? 'rgba(17,27,45,.82)' : 'rgba(255,255,255,.72)'};padding:9px 10px;text-align:left;cursor:pointer;"><div style="position:relative;width:36px;height:36px;flex:0 0 36px;border-radius:10px;display:grid;place-items:center;background:${isDarkMode ? '#151D2E' : '#FFF3CB'};border:1px solid ${border};font-size:18px;">🏆<span style="position:absolute;right:-4px;top:-4px;width:16px;height:16px;border-radius:999px;display:grid;place-items:center;background:${isDarkMode ? '#111B2D' : '#FFFDF8'};border:1px solid ${border};font-size:8px;">🌎</span></div><div style="min-width:0;flex:1;"><div style="font-size:11px;font-weight:900;line-height:1.2;color:${text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${name}</div><div style="margin-top:3px;font-size:9px;font-weight:800;line-height:1.2;color:${isDarkMode ? '#8CC8FF' : '#2F80ED'};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(status)}${timer ? ` · ${escapeHtml(timer)}` : ''}</div><div style="margin-top:3px;font-size:8.5px;font-weight:650;line-height:1.2;color:${muted};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(feeLabel)}${anglers ? ` · 👥 ${escapeHtml(anglers)}` : ''}</div></div><div style="font-size:16px;font-weight:900;color:${muted};">›</div></button>`;
  }).join('');

  root.innerHTML = `<div style="width:278px;padding:5px 4px 4px;font-family:inherit;"><div style="display:flex;align-items:center;gap:9px;padding:2px 2px 9px;"><div style="width:38px;height:38px;flex:0 0 38px;border-radius:12px;display:grid;place-items:center;background:${isDarkMode ? 'rgba(246,202,104,.10)' : '#FFF3CB'};border:1px solid ${border};font-size:19px;">🌎</div><div style="min-width:0;flex:1;"><div style="font-size:13px;font-weight:900;color:${text};">Worldwide Tournaments</div><div style="margin-top:2px;font-size:9.5px;font-weight:650;color:${muted};">${rows.length} global event${rows.length === 1 ? '' : 's'} · playable anywhere</div></div></div><div style="display:grid;gap:7px;">${rowHtml}</div><button type="button" data-roddo-open-world-dock="true" style="margin-top:9px;width:100%;border:1px solid ${isDarkMode ? 'rgba(244,202,104,.30)' : 'rgba(47,128,237,.20)'};border-radius:10px;background:${isDarkMode ? '#F5D77C' : '#2F80ED'};color:${isDarkMode ? '#42300A' : '#FFFFFF'};padding:9px 10px;font-size:10px;font-weight:850;cursor:pointer;">Open Tournament Dock</button></div>`;

  root.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const tournamentButton = target.closest?.('[data-roddo-open-world-tournament]') as HTMLElement | null;
    if (tournamentButton) {
      event.preventDefault();
      event.stopPropagation();
      const id = String(tournamentButton.getAttribute('data-roddo-open-world-tournament') || '').trim();
      if (!id) return;
      try { marker.closePopup?.(); } catch {}
      onOpenTournament(id);
      return;
    }
    const dockButton = target.closest?.('[data-roddo-open-world-dock]') as HTMLElement | null;
    if (dockButton) {
      event.preventDefault();
      event.stopPropagation();
      try { marker.closePopup?.(); } catch {}
      onOpenTournamentDock();
    }
  });

  try {
    (L as any).DomEvent?.disableClickPropagation?.(root);
    (L as any).DomEvent?.disableScrollPropagation?.(root);
  } catch {}
  return root;
};

export function createTournamentLayerController(): TournamentLayerController {
  const markerCache = new Map<string, MarkerCacheEntry>();
  const openPopupIds = new Set<string>();

  const clear = (layer?: any | null) => {
    markerCache.forEach((entry) => {
      try { layer?.removeLayer?.(entry.marker); } catch {
        try { entry.marker?.remove?.(); } catch {}
      }
    });
    markerCache.clear();
    openPopupIds.clear();
    try { layer?.clearLayers?.(); } catch {}
  };

  const closePopups = () => {
    markerCache.forEach((entry) => {
      try { entry.marker?.closePopup?.(); } catch {}
    });
  };

  const forEachMarker = (callback: (marker: any, key: string) => void) => {
    markerCache.forEach((entry, key) => callback(entry.marker, key));
  };

  const getMarkers = () => Array.from(markerCache.values()).map((entry) => entry.marker).filter(Boolean);

  const resetForLayerRebuild = () => {
    markerCache.clear();
    openPopupIds.clear();
  };

  const getWorldLatLng = (options: TournamentLayerSyncOptions): LatLng | null => {
    const { map, userLatLng, getOccupiedMapPoints, clampPointToSafeBox, resolveMapMarkerCollision, getSafeInsetsPx } = options;
    if (!userLatLng || !map) return null;
    try {
      const size = map.getSize?.();
      const point = map.latLngToContainerPoint?.([userLatLng.lat, userLatLng.lng]);
      if (!size || !point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return null;
      const inset = getSafeInsetsPx();
      const occupied = getOccupiedMapPoints(map);
      markerCache.forEach((entry, key) => {
        if (String(key) === WORLD_CACHE_KEY) return;
        try {
          const ll = entry?.marker?.getLatLng?.();
          if (!ll) return;
          const p = map.latLngToContainerPoint?.([Number(ll.lat), Number(ll.lng)]);
          if (Number.isFinite(p?.x) && Number.isFinite(p?.y)) occupied.push({ x: Number(p.x), y: Number(p.y) });
        } catch {}
      });
      const offsets = [
        { x: 92, y: 72 }, { x: -92, y: 72 }, { x: 92, y: -72 }, { x: -92, y: -72 },
        { x: 124, y: 4 }, { x: -124, y: 4 }, { x: 8, y: 112 }, { x: 8, y: -112 },
      ];
      const clearance = (candidate: MapPoint) => occupied.length
        ? Math.min(...occupied.map((other) => Math.hypot(candidate.x - other.x, candidate.y - other.y)))
        : Number.POSITIVE_INFINITY;
      let best: MapPoint | null = null;
      let bestScore = -1;
      for (const offset of offsets) {
        const candidate = { x: point.x + offset.x, y: point.y + offset.y };
        const inside = candidate.x >= inset.left + 30 && candidate.x <= size.x - inset.right - 30 && candidate.y >= inset.top + 30 && candidate.y <= size.y - inset.bottom - 36;
        if (!inside) continue;
        const score = clearance(candidate);
        if (score > bestScore) { best = candidate; bestScore = score; }
      }
      const preferred = best || clampPointToSafeBox({ x: point.x + 92, y: point.y + 72 }, size);
      const target = resolveMapMarkerCollision(preferred, occupied, size, { minDistance: 78, maxRadius: 118, seed: 271 });
      const ll = map.containerPointToLatLng?.([target.x, target.y]);
      const lat = Number(ll?.lat);
      const lng = Number(ll?.lng);
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    } catch { return null; }
  };

  const sync = (options: TournamentLayerSyncOptions) => {
    const {
      map, layer, localRows, worldRows, hideAll, isDarkMode, nowMs, participation = {}, resultsSeenRevision = 0,
      getTournamentLatLng, getPresentation, getScopeBadge, focusMapCardNearMarker, onBeforeLocalMarkerOpen,
      onBeforeWorldMarkerOpen, onOpenTournament, onOpenTournamentDock, onMarkResultsSeen,
    } = options;
    if (!map || !layer) return;

    if (hideAll) {
      clear(layer);
      return;
    }

    const nextIds = new Set<string>();

    for (const row of localRows || []) {
      const tournament = row?.tournament;
      const presentation = row?.presentation || getPresentation(tournament);
      const id = String(tournament?.id || '').trim();
      const ll = getTournamentLatLng(tournament);
      if (!id || !ll) continue;
      nextIds.add(id);
      const signature = JSON.stringify({
        status: tournament?.status || '',
        name: tournament?.name || '',
        locationLabel: tournament?.locationLabel || tournament?.mapLocation?.label || '',
        dark: !!isDarkMode,
        participantStatus: String(participation[id]?.status || ''),
        mapBadge: presentation?.badge || '',
        mapTimer: presentation?.timer || '',
        resultsSeenRevision,
      });
      const popupHtml = buildTournamentPopupHtml(tournament, id, presentation, participation, false, isDarkMode, nowMs);
      const cached = markerCache.get(id);
      if (cached) {
        try { cached.marker.setLatLng?.([ll.lat, ll.lng]); } catch {}
        if (cached.signature !== signature) {
          try { cached.marker.setIcon?.(createTournamentMarkerIcon(tournament, presentation, getScopeBadge(tournament), isDarkMode, nowMs)); } catch {}
          try { cached.marker.setPopupContent?.(popupHtml); } catch {}
          if (openPopupIds.has(id)) window.setTimeout(() => setMarkerBadgeVisible(cached.marker, false), 0);
          cached.signature = signature;
        }
        continue;
      }

      const marker = (L as any).marker([ll.lat, ll.lng], {
        icon: createTournamentMarkerIcon(tournament, presentation, getScopeBadge(tournament), isDarkMode, nowMs),
        pane: 'markersPane', keyboard: true, riseOnHover: true, bubblingMouseEvents: false,
        zIndexOffset: 1280 + Number(presentation?.priority || 0) * 120,
      });
      marker.on('click', (event: any) => {
        try {
          (L as any).DomEvent?.stopPropagation?.(event?.originalEvent || event);
          (L as any).DomEvent?.preventDefault?.(event?.originalEvent || event);
        } catch {}
        onBeforeLocalMarkerOpen();
        window.setTimeout(() => { try { marker.openPopup?.(); } catch {} }, 0);
      });
      marker.bindPopup?.(popupHtml, { closeButton: true, autoPan: false, maxWidth: 270, offset: [0, -72] });
      marker.on('popupopen', (event: any) => {
        openPopupIds.add(id);
        setMarkerBadgeVisible(marker, false);
        window.setTimeout(() => {
          try {
            const node = event?.popup?.getElement?.() as HTMLElement | null;
            if (!node) return;
            focusMapCardNearMarker(map, marker, node, { preferredGap: 32, focusBias: 0.66 });
            try {
              (L as any).DomEvent?.disableClickPropagation?.(node);
              (L as any).DomEvent?.disableScrollPropagation?.(node);
            } catch {}
            const selector = `[data-roddo-open-tournament="${CSS.escape(id)}"]`;
            const button = node.querySelector?.(selector) as HTMLButtonElement | null;
            if (!button) return;
            button.onclick = (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              try { (ev as any).stopImmediatePropagation?.(); } catch {}
              if (getPresentation(tournament)?.badge === 'RESULTS') onMarkResultsSeen(id);
              try { marker.closePopup?.(); } catch {}
              onOpenTournament(id);
            };
          } catch (err) {
            console.warn('[map] tournament popup action wiring failed', err);
          }
        }, 0);
      });
      marker.on('popupclose', () => {
        openPopupIds.delete(id);
        setMarkerBadgeVisible(marker, true);
      });
      marker.addTo(layer);
      markerCache.set(id, { marker, signature });
    }

    if ((worldRows || []).length) {
      const worldLl = getWorldLatLng(options);
      if (worldLl) {
        nextIds.add(WORLD_CACHE_KEY);
        const signature = JSON.stringify({ count: worldRows.length, ids: worldRows.map(({ tournament }) => String(tournament?.id || '')).join('|'), dark: !!isDarkMode });
        const cached = markerCache.get(WORLD_CACHE_KEY);
        if (cached) {
          try { cached.marker.setLatLng?.([worldLl.lat, worldLl.lng]); } catch {}
          if (cached.signature !== signature) {
            try { cached.marker.setIcon?.(createWorldIcon(worldRows.length, isDarkMode)); } catch {}
            try { cached.marker.setPopupContent?.(createWorldPopupContent(cached.marker, worldRows, isDarkMode, onOpenTournament, onOpenTournamentDock)); } catch {}
            cached.signature = signature;
          }
        } else {
          const marker = (L as any).marker([worldLl.lat, worldLl.lng], {
            icon: createWorldIcon(worldRows.length, isDarkMode), pane: 'markersPane', keyboard: true,
            riseOnHover: true, bubblingMouseEvents: false, zIndexOffset: 1480,
          });
          marker.bindPopup?.(createWorldPopupContent(marker, worldRows, isDarkMode, onOpenTournament, onOpenTournamentDock), {
            closeButton: true, autoPan: false, maxWidth: 310, offset: [0, -68], className: 'roddo-world-tournament-near-user-popup',
          });
          marker.on('click', (event: any) => {
            try {
              (L as any).DomEvent?.stopPropagation?.(event?.originalEvent || event);
              (L as any).DomEvent?.preventDefault?.(event?.originalEvent || event);
            } catch {}
            onBeforeWorldMarkerOpen();
            window.setTimeout(() => { try { marker.openPopup?.(); } catch {} }, 0);
          });
          marker.on('popupopen', (event: any) => {
            window.setTimeout(() => {
              try {
                const node = event?.popup?.getElement?.() as HTMLElement | null;
                if (!node) return;
                focusMapCardNearMarker(map, marker, node, { preferredGap: 28, focusBias: 0.64 });
              } catch {}
            }, 0);
          });
          marker.addTo(layer);
          markerCache.set(WORLD_CACHE_KEY, { marker, signature });
        }
      }
    }

    Array.from(markerCache.keys()).forEach((id) => {
      if (nextIds.has(id)) return;
      const cached = markerCache.get(id);
      try { layer.removeLayer?.(cached?.marker); } catch {}
      markerCache.delete(id);
      openPopupIds.delete(id);
    });
  };

  const dispose = (layer?: any | null) => clear(layer);
  return { sync, clear, closePopups, forEachMarker, getMarkers, resetForLayerRebuild, dispose };
}
